import React, { useState } from 'react';

import { PatchDongleParameters } from '../../../../../typings/api/skymap/rest/v1/dongle';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../utils/typescript-utils';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { InfoBox } from '../info-box/info-box';
import { Content } from '../integrations/providers/style';
import { IntegrationItem } from '../integrations/state/integrations-state';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { RegisteredDongle } from './dongle-list-company-admin';

interface Props {
  selectedDongle: RegisteredDongle | undefined;
  onClose: () => void;
  loadDonglesConnection: (
    skyMapProjectId: string,
    companyId: string | null,
  ) => Promise<IntegrationItem | undefined>;
}

const renameDongle = async (dongleId: string, name: string) => {
  const dongleService = SkyMapAxiosServiceFactory.instance.createDongleServiceV1();
  return await dongleService.patchDongle({
    path: { dongleId },
    body: { name },
  });
};

export const EditDongleRegistrationDialog = ({
  selectedDongle,
  onClose,
  loadDonglesConnection,
}: Props) => {
  if (!selectedDongle) {
    throw new Error("'EditDongleRegistrationDialog' requires a dongle to exist in props");
  }

  const [editInProgress, setEditInProgress] = useState(false);
  const [dongleName, setDongleName] = useState(selectedDongle.name ?? selectedDongle.id);
  const {
    handleError,
    clearErrorForPath,
    buildErrorList,
    getErrorForPath,
    hasError,
    excludeErrorPaths,
    includeErrorPaths,
  } = useErrorHandling<RequestParamsToApiPaths<PatchDongleParameters>>();

  const updateDongleAndCloseConfirmationForm = async () => {
    setEditInProgress(true);
    if (selectedDongle.id) {
      try {
        await EditDongleRegistrationFunctions.renameDongle(selectedDongle.id, dongleName);
        await loadDonglesConnection(selectedDongle.projectId, selectedDongle.companyId);
        onClose();
      } catch (error) {
        handleError(error);
        setEditInProgress(false);
      }
    }
  };

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      useGoogleTranslate={true}
      width={500}
      onClose={onClose}
    >
      {{
        header: 'Ändra namn på enhet',
        content: (
          <Content>
            {hasError(excludeErrorPaths(['body.name'])) &&
              buildErrorList({
                filter: includeErrorPaths(['path.dongleId']),
                infoBoxProps: {
                  bottomMargin: true,
                },
              })}
            <Stack spacing={0.5}>
              <InfoBox color="yellow">
                Ange ett nytt namn för enheten med ID <strong>{selectedDongle.id}</strong>.
              </InfoBox>

              <LabelledContainer text={`Namn`}>
                {(formElementId) => (
                  <TextBox
                    errorMessage={getErrorForPath('body.name')}
                    id={formElementId()}
                    value={dongleName}
                    onChange={(e) => {
                      if (selectedDongle) {
                        setDongleName(e.target.value);
                        clearErrorForPath('body.name');
                      }
                    }}
                  />
                )}
              </LabelledContainer>
            </Stack>
          </Content>
        ),
        footer: {
          right: (
            <>
              <Button
                color={'primary'}
                loading={editInProgress}
                variant={'contained'}
                onClick={updateDongleAndCloseConfirmationForm}
              >
                Spara
              </Button>
              <Button color={'secondary'} variant={'contained'} onClick={onClose}>
                Avbryt
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export type EditDongleRegistrationDialogProps = Props;

export const EditDongleRegistrationFunctions = {
  renameDongle: renameDongle,
};
